import type { HudSnapshot } from "../game/engine/types";

interface Props {
  hud: HudSnapshot | null;
  selfName: string;
  opponent?: { name: string; survivedMs: number; alive: boolean } | null;
}

export function HUD({ hud, selfName, opponent }: Props) {
  return (
    <div className="pointer-events-none absolute inset-x-0 top-0 z-20 flex flex-col gap-2 p-3 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <Panel>
          <PlayerLine
            label={selfName}
            value={hud ? hud.score.toLocaleString() : "0"}
            highlight
          />
          <LivesRow lives={hud?.lives ?? 0} />
        </Panel>
        {opponent && (
          <Panel align="end">
            <PlayerLine
              label={opponent.name}
              value={Math.floor((opponent.survivedMs / 1000) * 10).toString()}
            />
            <div className="mt-1 text-xs uppercase tracking-widest text-white/60">
              {opponent.alive ? "Still Running" : "Knocked Out"}
            </div>
          </Panel>
        )}
      </div>

      {hud && (
        <div className="mx-auto mt-1 flex max-w-xl flex-wrap items-center justify-center gap-2 text-[11px] font-display uppercase tracking-widest text-white/80">
          <Pill label="Combo" value={`x${hud.combo.toFixed(1)}`} accent="bg-cap-gold/20 text-cap-gold" />
          <Pill label="Shields" value={`${hud.shieldCharges}`} accent="bg-cap-red/20 text-cap-red" />
          <Pill label="Speed" value={`${Math.round(hud.speed)}`} accent="bg-cap-blue/30 text-white" />
          {hud.multiplierActive && (
            <Pill label="x2" value="ACTIVE" accent="bg-yellow-400/30 text-yellow-200" />
          )}
          {hud.invincible && (
            <Pill label="Shield" value="GUARD" accent="bg-emerald-400/30 text-emerald-200" />
          )}
        </div>
      )}
    </div>
  );
}

function Panel({ children, align }: { children: React.ReactNode; align?: "end" }) {
  return (
    <div
      className={[
        "min-w-[160px] rounded-xl border border-white/15 bg-cap-deep/70 px-4 py-2 shadow-lg backdrop-blur",
        align === "end" ? "text-right" : "",
      ].join(" ")}
    >
      {children}
    </div>
  );
}

function PlayerLine({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="font-display text-xs uppercase tracking-widest text-white/60">{label}</span>
      <span
        className={[
          "font-display text-3xl leading-none",
          highlight ? "text-cap-gold drop-shadow-[0_0_8px_rgba(240,179,35,0.6)]" : "text-white",
        ].join(" ")}
      >
        {value}
      </span>
    </div>
  );
}

function LivesRow({ lives }: { lives: number }) {
  return (
    <div className="mt-1 flex items-center gap-1">
      {Array.from({ length: 3 }).map((_, i) => (
        <span
          key={i}
          className={[
            "h-3 w-3 rounded-full",
            i < lives ? "bg-cap-red shadow-[0_0_6px_rgba(200,16,46,0.8)]" : "bg-white/15",
          ].join(" ")}
        />
      ))}
    </div>
  );
}

function Pill({ label, value, accent }: { label: string; value: string; accent: string }) {
  return (
    <span className={`rounded-full px-3 py-1 ${accent}`}>
      <span className="opacity-70">{label}</span> <span className="font-bold">{value}</span>
    </span>
  );
}
